import React, { useState, useEffect, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Plus, Edit, Trash2, Home, Check, X, ImageIcon, Video, Loader2 } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import roomService, { Room, CreateRoomData } from '../services/roomService'

type RoomFormState = Omit<CreateRoomData, 'capacity'> & { capacity: number | '' }
import mediaService from '../services/mediaService'
import toast from 'react-hot-toast'
import ConfirmationDialog from '../components/ConfirmationDialog'
import { MEDIA_SECTIONS, validateImageFiles, validateVideoFile } from '../config/mediaValidation'
import { validateRoom } from '../validation'

const roomTypes = [
  { value: 'PRIVATE', label: 'Private', capacity: 1 },
  { value: 'DOUBLE', label: 'Shared - Double', capacity: 2 },
  { value: 'TRIPLE', label: 'Shared - Triple', capacity: 3 },
  { value: 'QUADRUPLE', label: 'Shared - Quadruple', capacity: 4 },
  { value: 'CUSTOM', label: 'Custom', capacity: null }, // null means user can set custom capacity
]

const Rooms: React.FC = () => {
  const navigate = useNavigate()
  const { selectedSchool, schools, loading: authLoading, selectSchool } = useAuth()
  const [rooms, setRooms] = useState<Room[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [formData, setFormData] = useState<RoomFormState>({
    type: 'PRIVATE',
    name: '',
    capacity: 1,
    notes: '',
    images: [],
    video: '',
    isActive: true,
  })
  const [uploadingImage, setUploadingImage] = useState(false)
  const [uploadingVideo, setUploadingVideo] = useState(false)
  const imageInputRef = useRef<HTMLInputElement>(null)
  const videoInputRef = useRef<HTMLInputElement>(null)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [roomToDelete, setRoomToDelete] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [, setErrors] = useState<Record<string, string>>({})
  const [roomMediaUrlMap, setRoomMediaUrlMap] = useState<Record<string, string>>({})
  const fetchingRef = useRef(false) // Prevent duplicate calls

  /** Resolve room image/video URL or fileKey to a signed display URL (CloudFront is private) */
  const getRoomMediaDisplayUrl = useCallback((value: string): string => {
    if (!value) return value
    const fileKey = value.startsWith('http://') || value.startsWith('https://')
      ? mediaService.extractFileKeyFromUrl(value)
      : value
    if (!fileKey) return value
    return roomMediaUrlMap[fileKey] || value
  }, [roomMediaUrlMap])

  const fetchRooms = useCallback(async () => {
    if (!selectedSchool || fetchingRef.current) {
      if (!selectedSchool) {
        setLoading(false)
      }
      return
    }

    fetchingRef.current = true
    setLoading(true)
    try {
      const data = await roomService.getSchoolRooms(selectedSchool._id)
      if (Array.isArray(data)) {
        setRooms(data)
      } else {
        setRooms([])
      }
    } catch (error: any) {
      console.error('Error fetching rooms:', error?.response?.data ?? error)
      toast.error(error.response?.data?.message || 'Failed to load rooms')
      setRooms([]) // Set empty array on error
    } finally {
      setLoading(false)
      fetchingRef.current = false
    }
  }, [selectedSchool?._id])

  // Auto-select first school if available
  useEffect(() => {
    if (!authLoading && !selectedSchool && schools && schools.length > 0) {
      selectSchool(schools[0]._id)
    }
  }, [authLoading, selectedSchool, schools, selectSchool])

  // Fetch rooms when school is selected
  useEffect(() => {
    if (!authLoading && selectedSchool) {
      fetchRooms()
    } else if (!authLoading && !selectedSchool) {
      setLoading(false)
    }
  }, [authLoading, selectedSchool?._id, fetchRooms])

  // Build display URLs from file keys (domain + key)
  useEffect(() => {
    const collect = (value: string): string | null => {
      if (!value) return null
      if (value.startsWith('http://') || value.startsWith('https://')) {
        return mediaService.extractFileKeyFromUrl(value)
      }
      return value
    }
    const fileKeys = new Set<string>()
    rooms.forEach(room => {
      room.images?.forEach(url => { const k = collect(url); if (k) fileKeys.add(k) })
      if (room.video) { const k = collect(room.video); if (k) fileKeys.add(k) }
    })
    if (showModal) {
      formData.images?.forEach(url => { const k = collect(url); if (k) fileKeys.add(k) })
      if (formData.video) { const k = collect(formData.video); if (k) fileKeys.add(k) }
    }
    const map: Record<string, string> = {}
    fileKeys.forEach((key) => {
      map[key] = mediaService.getMediaUrlFromKey(key)
    })
    setRoomMediaUrlMap(map)
  }, [rooms, showModal, formData.images, formData.video])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedSchool) return

    const capacityNum = formData.capacity === '' ? NaN : formData.capacity
    const roomErrors = validateRoom({ ...formData, capacity: capacityNum })
    if (Object.keys(roomErrors).length > 0) {
      const firstError = Object.values(roomErrors)[0]
      toast.error(firstError)
      return
    }

    try {
      await roomService.createRoom(selectedSchool._id, { ...formData, capacity: capacityNum })
      toast.success('Room created successfully')
      setShowModal(false)
      resetForm()
      fetchRooms()
    } catch (error) {
      console.error('Error saving room:', error)
      toast.error('Failed to save room')
    }
  }

  const handleEdit = (room: Room) => {
    navigate(`/rooms/${room._id}`)
  }

  const handleDelete = (roomId: string) => {
    setRoomToDelete(roomId)
    setShowDeleteDialog(true)
  }

  const handleDeleteConfirm = async () => {
    if (!roomToDelete) return

    try {
      setDeleting(true)
      await roomService.deleteRoom(roomToDelete)
      toast.success('Room deleted successfully')
      fetchRooms()
      setShowDeleteDialog(false)
      setRoomToDelete(null)
    } catch (error) {
      toast.error('Failed to delete room')
    } finally {
      setDeleting(false)
    }
  }

  const handleDeleteCancel = () => {
    setShowDeleteDialog(false)
    setRoomToDelete(null)
  }

  const handleToggleStatus = async (roomId: string) => {
    try {
      await roomService.toggleRoomStatus(roomId)
      toast.success('Room status updated')
      fetchRooms()
    } catch (error) {
      console.error('Error toggling room status:', error)
      toast.error('Failed to update room status')
    }
  }

  const resetForm = () => {
    setFormData({
      type: 'PRIVATE',
      name: '',
      capacity: 1,
      notes: '',
      images: [],
      video: '',
      isActive: true,
    })
    setErrors({})
  }

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files?.length || !selectedSchool) return
    const fileList = Array.from(files)
    const currentCount = formData.images?.length ?? 0
    const validation = validateImageFiles(fileList, 'roomImages', currentCount)
    if (!validation.valid) {
      toast.error(validation.error || 'Validation failed')
      e.target.value = ''
      return
    }
    setUploadingImage(true)
    try {
      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i]
        const result = await mediaService.uploadFile(file, 'generalImage', selectedSchool._id)
        if (result?.data?.fileKey) {
          setFormData(prev => ({
            ...prev,
            images: [...(prev.images || []), result.data.fileKey],
          }))
        }
      }
      toast.success('Image(s) uploaded')
    } catch (err: any) {
      toast.error(err?.message || 'Failed to upload image')
    } finally {
      setUploadingImage(false)
      e.target.value = ''
    }
  }

  const handleVideoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !selectedSchool) return
    const validation = validateVideoFile(file, 'roomVideo', formData.video ? 1 : 0)
    if (!validation.valid) {
      toast.error(validation.error || 'Validation failed')
      e.target.value = ''
      return
    }
    setUploadingVideo(true)
    try {
      const result = await mediaService.uploadFile(file, 'generalVideo', selectedSchool._id)
      if (result?.data?.fileKey) {
        setFormData(prev => ({ ...prev, video: result.data.fileKey }))
        toast.success('Video uploaded')
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to upload video')
    } finally {
      setUploadingVideo(false)
      e.target.value = ''
    }
  }

  const removeImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: (prev.images || []).filter((_, i) => i !== index),
    }))
  }

  const removeVideo = () => {
    setFormData(prev => ({ ...prev, video: '' }))
  }

  const openCreateModal = () => {
    resetForm()
    setShowModal(true)
  }

  if (authLoading) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Loading...</p>
      </div>
    )
  }

  if (!selectedSchool) {
    if (schools && schools.length > 0) {
      return (
        <div className="text-center py-12">
          <p className="text-gray-500">Please select a school from the profile menu</p>
        </div>
      )
    }
    return (
      <div className="text-center py-12 bg-white rounded-lg border border-gray-200 p-8">
        <Home className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Schools Available</h3>
        <p className="text-gray-500 mb-4">
          You don't have any schools associated with your account yet.
        </p>
        <p className="text-sm text-gray-400">
          Please contact support to set up your school profile, or check your account settings.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-r from-primary-500 via-primary-600 to-primary-700 shadow-2xl rounded-2xl">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-600/20 via-primary-700/20 to-primary-800/20"></div>
        <div className="relative flex items-center justify-between p-4">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-xl ring-2 ring-white/30">
                <Home className="h-6 w-6 text-white" />
              </div>
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-r from-emerald-400 to-cyan-400 rounded-full border-2 border-white shadow-lg"></div>
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">🏠 Accommodation</h1>
              <p className="text-white/90 text-sm">Manage room types for {selectedSchool.name}</p>
            </div>
          </div>
          <button
            onClick={openCreateModal}
            className="flex items-center px-4 py-2 text-sm font-semibold text-white bg-white/20 backdrop-blur-sm rounded-xl hover:bg-white/30 transition-all duration-300 border border-white/30 shadow-lg hover:shadow-xl hover:scale-105"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Room Type
          </button>
        </div>
      </div>

      {/* Accommodation List */}
      {loading ? (
        <div className="text-center py-12">
          <p className="text-gray-500">Loading rooms...</p>
        </div>
      ) : !rooms || rooms.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <Home className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">No rooms yet</h3>
          <p className="mt-2 text-sm text-gray-500">Get started by creating your first room type.</p>
          <button
            onClick={openCreateModal}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus size={20} />
            Add Room Type
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rooms.map((room, index) => {
            if (!room?.type) return null
            return (
            <motion.div
              key={room._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              className="bg-white rounded-lg border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all duration-300"
            >
              <div className="p-4">
                {/* Thumbnail if room has images (signed URL for private CloudFront) */}
                {room.images?.length ? (
                  <div className="mb-3 rounded overflow-hidden border border-gray-200">
                    <img
                      src={getRoomMediaDisplayUrl(room.images[0])}
                      alt={room.name}
                      className="w-full h-24 object-cover"
                    />
                  </div>
                ) : null}
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{room.name}</h3>
                    <p className="text-sm text-gray-600 capitalize">{(room?.type ?? '').toLowerCase().replace('-', ' ')}</p>
                  </div>
                  <button
                    onClick={() => handleToggleStatus(room._id)}
                    className={`p-1 rounded transition-colors ${
                      room.isActive
                        ? 'text-emerald-600 hover:bg-emerald-50'
                        : 'text-gray-400 hover:bg-gray-50'
                    }`}
                    title={room.isActive ? 'Active' : 'Inactive'}
                  >
                    {room.isActive ? <Check size={18} /> : <X size={18} />}
                  </button>
                </div>

                {/* Details */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Capacity:</span>
                    <span className="font-medium text-gray-900">{room.capacity} people</span>
                  </div>
                  {room.notes && (
                    <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded border-l-2 border-gray-300">
                      {room.notes}
                    </div>
                  )}
                  {(room.images?.length ?? 0) > 0 && (
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <ImageIcon className="h-3.5 w-3.5" />
                      {room.images!.length} image(s)
                    </div>
                  )}
                  {room.video && (
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <Video className="h-3.5 w-3.5" />
                      Video
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-3 border-t border-gray-100">
                  <button
                    onClick={() => handleEdit(room)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-primary-600 hover:bg-primary-50 rounded transition-colors"
                  >
                    <Edit size={16} />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(room._id)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded transition-colors"
                  >
                    <Trash2 size={16} />
                    Delete
                  </button>
                </div>
              </div>
            </motion.div>
          )
          })}
        </div>
      )}

      {/* Create Room Modal - portaled so overlay covers from top and isn't clipped */}
      {showModal && createPortal(
        <div
          className="fixed inset-0 top-0 left-0 right-0 bottom-0 bg-black/50 flex items-center justify-center z-[9999] p-4"
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg max-w-md w-full max-h-[90vh] flex flex-col shadow-xl"
          >
            <h2 className="text-2xl font-bold text-gray-900 p-6 pb-0 flex-shrink-0">
              Create New Room
            </h2>

            <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 p-6">
              <div className="space-y-4 overflow-y-auto flex-1 min-h-0 pr-1 -mr-1">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Room Type *
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => {
                    const selectedType = e.target.value as any
                    const selectedRoomType = roomTypes.find(rt => rt.value === selectedType)
                    setFormData({
                      ...formData,
                      type: selectedType,
                      capacity:
                        selectedRoomType?.capacity !== null
                          ? selectedRoomType?.capacity ?? 1
                          : formData.capacity === '' || typeof formData.capacity === 'number'
                            ? formData.capacity
                            : 1,
                    })
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 focus:border-primary-500"
                  required
                >
                  {roomTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Room Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g., Deluxe Private Room"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Capacity (People) *
                </label>
                <input
                  type="number"
                  value={
                    formData.type === 'CUSTOM' && formData.capacity === '' ? '' : formData.capacity
                  }
                  onChange={(e) => {
                    if (formData.type !== 'CUSTOM') return
                    const v = e.target.value
                    if (v === '') {
                      setFormData({ ...formData, capacity: '' })
                      return
                    }
                    const n = parseInt(v, 10)
                    if (!Number.isNaN(n)) setFormData({ ...formData, capacity: n })
                  }}
                  onWheel={(e) => (e.target as HTMLInputElement).blur()}
                  readOnly={formData.type !== 'CUSTOM'}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md ${
                    formData.type === 'CUSTOM' 
                      ? 'bg-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 focus:border-primary-500' 
                      : 'bg-gray-50 text-gray-600 cursor-not-allowed'
                  }`}
                  min="1"
                  max="20"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.type === 'CUSTOM' 
                    ? 'Enter the number of people this room can accommodate' 
                    : 'Capacity is automatically set based on room type'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 focus:border-primary-500"
                  rows={3}
                  placeholder="Additional information about this room type..."
                />
              </div>

              {/* Room images */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Room images
                </label>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept={MEDIA_SECTIONS.roomImages.acceptImages}
                  multiple
                  className="hidden"
                  onChange={handleImageSelect}
                />
                <button
                  type="button"
                  onClick={() => imageInputRef.current?.click()}
                  disabled={uploadingImage}
                  className="flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-md text-gray-600 hover:border-primary-400 hover:bg-primary-50/50 transition-colors disabled:opacity-50"
                >
                  {uploadingImage ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ImageIcon className="h-4 w-4" />
                  )}
                  {uploadingImage ? 'Uploading...' : 'Upload images'}
                </button>
                {(formData.images?.length ?? 0) > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {(formData.images || []).map((url, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={getRoomMediaDisplayUrl(url)}
                          alt={`Room ${index + 1}`}
                          className="h-16 w-16 object-cover rounded border border-gray-200"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute -top-1 -right-1 p-0.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Room video */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Room video
                </label>
                <input
                  ref={videoInputRef}
                  type="file"
                  accept={MEDIA_SECTIONS.roomVideo.acceptVideos}
                  className="hidden"
                  onChange={handleVideoSelect}
                />
                <button
                  type="button"
                  onClick={() => videoInputRef.current?.click()}
                  disabled={uploadingVideo}
                  className="flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-md text-gray-600 hover:border-primary-400 hover:bg-primary-50/50 transition-colors disabled:opacity-50"
                >
                  {uploadingVideo ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Video className="h-4 w-4" />
                  )}
                  {uploadingVideo ? 'Uploading...' : 'Upload video'}
                </button>
                {formData.video && (
                  <div className="mt-2 flex items-center gap-2">
                    <Video className="h-5 w-5 text-primary-600" />
                    <span className="text-sm text-gray-600">Video added</span>
                    <button
                      type="button"
                      onClick={removeVideo}
                      className="text-red-600 hover:text-red-700 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">
                  Active (available for selection)
                </label>
              </div>
              </div>

              <div className="flex gap-3 pt-4 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false)
                    resetForm()
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                >
                  Create
                </button>
              </div>
            </form>
          </motion.div>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Room"
        message="Are you sure you want to delete this room? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonColor="red"
        isLoading={deleting}
      />
    </div>
  )
}

export default Rooms

